import React, { Suspense, lazy, useEffect, useState, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import { supabase } from './supabase'; // Asegúrate de que la ruta sea correcta

import PlanGate from './components/PlanGate';
import { TenantProvider, useTenant } from './context/TenantContext';
import { isModuleHiddenForManager } from './utils/managerAccess';
import NativeDeepLinks from './components/NativeDeepLinks';

const Layout = lazy(() => import('./components/Layout'));
const AdminChat = lazy(() => import('./components/AdminChat'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./pages/DashboardPanel'));
const Services = lazy(() => import('./pages/Services'));
const Employees = lazy(() => import('./pages/Employees'));
const Customers = lazy(() => import('./pages/Customers'));
const Reports = lazy(() => import('./pages/Reports'));
const AuditHistoryPage = lazy(() => import('./pages/AuditHistoryPage'));
const DutyRangeFix = lazy(() => import('./pages/DutyRangeFix'));
const Expenses = lazy(() => import('./pages/Expenses'));
const Promotions = lazy(() => import('./pages/Promotions'));
const CustomerFeedback = lazy(() => import('./pages/CustomerFeedback'));
const CustomerPortal = lazy(() => import('./pages/CustomerPortal'));
const CustomerLogin = lazy(() => import('./pages/CustomerLogin'));
const PortalRedirect = lazy(() => import('./pages/PortalRedirect'));
const CustomerPortalByToken = () => {
    const { token } = useParams();
    return <CustomerPortal key={token} />;
};
const SmartRoot = lazy(() => import('./pages/SmartRoot'));
const MembershipSettings = lazy(() => import('./pages/MembershipSettings'));
const BusinessSettings = lazy(() => import('./pages/BusinessSettings'));
const RegisterBusiness = lazy(() => import('./pages/RegisterBusiness'));
const OnboardingWizard = lazy(() => import('./pages/OnboardingWizard'));
const SuperAdminPanel = lazy(() => import('./pages/SuperAdminPanel'));
const SupervisorPanel = lazy(() => import('./pages/SupervisorPanel'));
const WhatsAppManager = lazy(() => import('./pages/WhatsAppManager'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const PublicGiftCardPage = lazy(() => import('./pages/PublicGiftCardPage'));
const Commissions = lazy(() => import('./pages/Commissions'));
const VerifyCoupon = lazy(() => import('./pages/VerifyCoupon'));
const Reservations = lazy(() => import('./pages/Reservations'));
const MembershipSubscribe = lazy(() => import('./pages/MembershipSubscribe'));
const Ticket = lazy(() => import('./pages/Ticket'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const SalesRepLogin = lazy(() => import('./pages/SalesRepLogin'));
const SalesRepPortal = lazy(() => import('./pages/SalesRepPortal'));
const DistributorRegister = lazy(() => import('./pages/DistributorRegister'));
const DistributorLogin = lazy(() => import('./pages/DistributorLogin'));
const DistributorPortal = lazy(() => import('./pages/DistributorPortal'));
const MaterialsStore = lazy(() => import('./pages/MaterialsStore'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const UpdatePassword = lazy(() => import('./pages/UpdatePassword'));

const FullScreenLoader = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#0f172a', color: 'white' }}>
        <div style={{ width: '40px', height: '40px', border: '4px solid #6366f1', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin{100%{transform:rotate(360deg)}}`}</style>
    </div>
);

// Permisos del gerente — la guardia de RUTA. Ocultar el item del menú no basta:
// la URL escrita a mano tiene que toparse con el candado igual (mismo criterio
// que el PlanGate de /reports). Mismo predicado que el menú (utils/managerAccess).
// Fail-open mientras cargan los settings — es control de UX, no de datos.
const ManagerModuleGate = ({ moduleId, children }) => {
    const { role, brandingSettings } = useTenant();
    if (isModuleHiddenForManager(role, brandingSettings, moduleId)) {
        return <Navigate to="/dashboard" replace />;
    }
    return children;
};

// Only show AI agent to admin-level users, not employees
const AdminChatGate = () => {
    const { role, hasWhatsAppAgent } = useTenant();
    const location = useLocation();
    // Sin plan de agente IA no hay burbuja (el server respondería 402 igual);
    // trial NO desbloquea el agente — ver progress/trial-whatsapp-agent/hallazgos.md
    if (role === 'employee' || !hasWhatsAppAgent || location.pathname === '/dashboard') return null;
    return <AdminChat />;
};

// Gate: loads the current user's org info and renders the OnboardingWizard
const OnboardingGate = () => {
    const navigate = useNavigate();
    const [org, setOrg] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user) { navigate('/login', { replace: true }); return; }

            const { data: profile } = await supabase
                .from('user_profiles')
                .select('organization_id, organizations(id, name, onboarding_completed)')
                .eq('id', session.user.id)
                .single();

            if (!profile) { navigate('/dashboard', { replace: true }); return; }

            const orgData = profile.organizations;
            if (orgData?.onboarding_completed) {
                navigate('/dashboard', { replace: true }); return;
            }
            setOrg(orgData);
            setLoading(false);
        };
        load();
    }, [navigate]);

    if (loading) return (
        <FullScreenLoader />
    );

    return <OnboardingWizard
        organizationId={org?.id}
        businessName={org?.name}
        onComplete={() => navigate('/dashboard', { replace: true })}
    />;
};

// Componente para proteger rutas

const RequireAuth = ({ children }) => {
    const { organizationId, loading: tenantLoading, refresh: refreshTenant } = useTenant();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const location = useLocation();
    const reparandoRef = useRef(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (loading) {
                setLoading(false);
                setError("Tiempo de espera agotado. Es posible que el servidor esté despertando tras un periodo de inactividad.");
            }
        }, 20000);

        supabase.auth.getSession().then(async ({ data: { session }, error }) => {
            if (error) {
                setError(error.message);
                setLoading(false);
                return;
            }

            if (session?.user) {
                const { data: employee } = await supabase
                    .from('employees')
                    .select('is_active')
                    .eq('user_id', session.user.id)
                    .single();

                if (employee && employee.is_active === false) {
                    await supabase.auth.signOut();
                    alert("Tu cuenta ha sido desactivada.");
                    setSession(null);
                } else {
                    setSession(session);
                }
            } else {
                setSession(null);
            }
            setLoading(false);
            clearTimeout(timer);
        });

        return () => clearTimeout(timer);
    }, []);

    // ── Rescate del alta a medias ─────────────────────────────────────────
    //
    // El alta NO es atómica: RegisterBusiness crea el usuario en Auth y DESPUÉS
    // llama a /api/register-business. Si esa segunda llamada falla —red, un 401,
    // el usuario cerrando la pestaña— el usuario de Auth ya existe y nada lo
    // repara: no hay trigger en auth.users. Y esta misma guarda lo deja pasar,
    // porque solo mira `session`, nunca `organizationId`. El dueño acaba en un
    // dashboard vacío, sin mensaje y sin salida.
    //
    // Ya pasó una vez: una cuenta del 2026-07-24 que quería crear "Jm Auto
    // Detailing & Windows tint" lleva desde entonces sin organización. Es 1 de
    // 12 altas por formulario desde marzo.
    //
    // Aquí se reintenta. Es seguro porque el endpoint YA es idempotente: si el
    // perfil existe devuelve la organización que hay, sin crear una segunda
    // (ver api/register-business.js, bloque "Idempotencia").
    //
    // Condiciones para no disparar de más:
    //   · hay sesión pero NO hay organizationId, con el tenant ya cargado
    //   · el usuario trae `business_name` en el metadata — o sea que vino del
    //     formulario de alta. Un empleado sin organización NO entra por aquí.
    //   · una sola vez por montaje (reparandoRef)
    useEffect(() => {
        if (loading || tenantLoading) return;
        if (!session?.user || organizationId) return;
        if (reparandoRef.current) return;

        const businessName = session.user.user_metadata?.business_name;
        if (!businessName) return;

        reparandoRef.current = true;
        (async () => {
            try {
                const { data: { session: s } } = await supabase.auth.getSession();
                if (!s?.access_token) return;

                const res = await fetch('/api/register-business', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${s.access_token}`,
                    },
                    body: JSON.stringify({
                        userId: session.user.id,
                        // Ya viene en minúsculas desde Auth; se normaliza igual
                        // porque el endpoint compara contra esto.
                        email: (session.user.email || '').trim().toLowerCase(),
                        businessName,
                    }),
                });

                if (res.ok) {
                    await refreshTenant?.();
                } else {
                    const cuerpo = await res.text().catch(() => '');
                    console.error('[alta] el reintento falló:', res.status, cuerpo);
                }
            } catch (e) {
                console.error('[alta] el reintento falló:', e);
            }
        })();
    }, [loading, tenantLoading, session, organizationId, refreshTenant]);

    if (loading || tenantLoading) {
        return (
            <FullScreenLoader />
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: 'orange', color: 'white', padding: '20px', textAlign: 'center' }}>
                <h2>🟠 Error de Conexión</h2>
                <p>{error}</p>
                <button onClick={() => window.location.reload()} style={{ marginTop: '20px', padding: '10px 20px' }}>Reintentar</button>
            </div>
        );
    }

    if (!session) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return children;
};

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ error, errorInfo });
        console.error("Uncaught error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '2rem', color: '#DC2626', backgroundColor: '#FEF2F2', minHeight: '100vh', fontFamily: 'monospace' }}>
                    <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>💥 CRASH DETECTED</h1>
                    <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '0.5rem', border: '1px solid #FCA5A5', overflow: 'auto' }}>
                        <h3 style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Error:</h3>
                        <pre style={{ whiteSpace: 'pre-wrap', marginBottom: '1rem' }}>
                            {this.state.error && this.state.error.toString()}
                        </pre>
                        <h3 style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Stack:</h3>
                        <pre style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                            {this.state.errorInfo && this.state.errorInfo.componentStack}
                        </pre>
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        style={{ marginTop: '2rem', padding: '1rem 2rem', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '0.5rem', fontSize: '1.2rem', cursor: 'pointer' }}
                    >
                        🔄 RECARGAR PÁGINA
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

const AppRoot = () => {
    return (
        <ErrorBoundary>
            <TenantProvider>
                {/* Universal links (iOS): /portal/t/<token> abre la app en el portal. No-op en web. */}
                <NativeDeepLinks />
                <Suspense fallback={<FullScreenLoader />}>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/update-password" element={<UpdatePassword />} />
                        <Route path="/customer-login" element={<CustomerLogin />} />
                        <Route path="/register" element={<RegisterBusiness />} />
                        <Route path="/onboarding" element={<OnboardingGate />} />
                        {/* Fuera del bloque con Layout (BillingPage maneja su propia sesión),
                            pero dentro del TenantProvider: la guardia del gerente aplica igual. */}
                        <Route path="/billing" element={<ManagerModuleGate moduleId="billing"><BillingPage /></ManagerModuleGate>} />

                        <Route path="/feedback/:transactionId" element={<CustomerFeedback />} />
                        {/* Capability link nuevo: token opaco en la URL */}
                        {/* key={token}: con universal links la app puede pasar de /portal/t/A a
                            /portal/t/B sin recargar; el portal guarda refs/estado por cliente
                            (vehículo elegido, historial "inicializado") que NO deben sobrevivir
                            al cambio de token. */}
                        <Route path="/portal/t/:token" element={<CustomerPortalByToken />} />
                        {/* Legacy: /portal/<uuid> → resuelve token → redirige al portal nuevo (gracia) */}
                        <Route path="/portal/:customerId" element={<PortalRedirect />} />
                        <Route path="/subscribe" element={<MembershipSubscribe />} />
                        <Route path="/ticket/:id" element={<Ticket />} />
                        <Route path="/gift/:orgId" element={<PublicGiftCardPage />} />
                        <Route path="/regalo/:orgId" element={<PublicGiftCardPage />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        {/* Alias en español — lo enlazan la tienda, el portal y el registro de distribuidores */}
                        <Route path="/terminos" element={<TermsOfService />} />
                        <Route path="/verify-coupon" element={<VerifyCoupon />} />
                        <Route path="/rep-login" element={<SalesRepLogin />} />
                        <Route path="/rep-portal" element={<SalesRepPortal />} />
                        <Route path="/distribuidores/registro" element={<DistributorRegister />} />
                        <Route path="/distribuidor/login" element={<DistributorLogin />} />
                        <Route path="/distribuidor" element={<DistributorPortal />} />

                        <Route path="/" element={<SmartRoot />} />

                        <Route
                            path="*"
                            element={
                                <RequireAuth>
                                    <Layout>
                                        <Routes>
                                            <Route path="/dashboard" element={<Dashboard />} />
                                            <Route path="/" element={<Navigate to="/dashboard" replace />} />
                                            <Route path="/services" element={<ManagerModuleGate moduleId="services"><Services /></ManagerModuleGate>} />
                                            <Route path="/employees" element={<ManagerModuleGate moduleId="employees"><Employees /></ManagerModuleGate>} />
                                            <Route path="/customers" element={<ManagerModuleGate moduleId="customers"><Customers /></ManagerModuleGate>} />
                                            <Route path="/reports" element={<ManagerModuleGate moduleId="reports"><PlanGate feature="reports"><Reports /></PlanGate></ManagerModuleGate>} />
                                            {/* Mismo gate que /reports: ocultar el link del menu no basta,
                                                la URL escrita a mano tiene que toparse con el candado igual. */}
                                            <Route path="/historial" element={<ManagerModuleGate moduleId="historial"><PlanGate feature="reports"><AuditHistoryPage /></PlanGate></ManagerModuleGate>} />
                                            {/* C1 — corrección de rango de la batuta. La pantalla se
                                                auto-protege (solo dueño); se llega desde Comisiones. */}
                                            <Route path="/batuta/rango" element={<DutyRangeFix />} />
                                            <Route path="/expenses" element={<ManagerModuleGate moduleId="expenses"><Expenses /></ManagerModuleGate>} />
                                            <Route path="/tienda-materiales" element={<ManagerModuleGate moduleId="materials"><MaterialsStore /></ManagerModuleGate>} />

                                            <Route path="/promotions" element={<ManagerModuleGate moduleId="promotions"><PlanGate feature="promotions"><Promotions /></PlanGate></ManagerModuleGate>} />
                                            <Route path="/memberships" element={<ManagerModuleGate moduleId="memberships"><PlanGate feature="memberships"><MembershipSettings /></PlanGate></ManagerModuleGate>} />
                                            <Route path="/settings" element={<ManagerModuleGate moduleId="settings"><BusinessSettings /></ManagerModuleGate>} />
                                            <Route path="/commissions" element={<Commissions />} />
                                            <Route path="/superadmin" element={<SuperAdminPanel />} />
                                            <Route path="/supervisor" element={<SupervisorPanel />} />
                                            <Route path="/whatsapp" element={<PlanGate feature="whatsapp_agent"><WhatsAppManager /></PlanGate>} />
                                            <Route path="/reservations" element={<ManagerModuleGate moduleId="reservations"><Reservations /></ManagerModuleGate>} />
                                            <Route path="*" element={<Navigate to="/" replace />} />
                                        </Routes>
                                    </Layout>
                                    <AdminChatGate />
                                </RequireAuth>
                            }
                        />
                    </Routes>
                </Suspense>
            </TenantProvider>
        </ErrorBoundary>
    );
};

export default AppRoot;
